import { useMemo, useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { filterPositions, matchedSerials } from "../../lib/pickerSearch";

export { filterPositions, matchedSerials };

/**
 * PositionPicker -- suchbare Combobox für Bestands-Positionen.
 * Zeigt pro Option: Artikelname (fett) · Fach-Code · verfügbare Menge.
 *
 * Suche (#174): Wortsuche statt Fuzzy-Matching. Jedes eingegebene Wort muss
 * im Artikelnamen, in der EAN, in der Hersteller-Nr., in einer Seriennummer
 * an der Position, im Fach-Code oder im Fach-Namen vorkommen (#182) --
 * Reihenfolge und Groß-/Kleinschreibung sind egal. Treffer stehen
 * alphabetisch nach Artikelname, dann Fach-Name und Fach-Code.
 *
 * Farben: Der Popover liegt außerhalb des Grain-Hintergrunds, dort misst axe
 * den Kontrast real. `text-muted-foreground` erreicht auf `bg-popover` im
 * Dark-Theme nur 2,75:1, deshalb hier `text-foreground/70` (>= 5:1 in beiden
 * Themes).
 *
 * Props:
 *   value, onChange, positions[], articles{}, compartments{}
 *   serialsByPosition{} -- position_id -> [Seriennummern der Assets dort]
 */

export function PositionPicker({
    value,
    onChange,
    positions,
    articles,
    compartments = {},
    serialsByPosition = {},
    placeholder = "Bestands-Position wählen",
    label = "Bestands-Position",
    testId = "position-picker",
    emptyLabel = "Keine passende Position.",
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    const current = useMemo(
        () => positions.find((p) => p.id === value) || null,
        [positions, value]
    );

    const filtered = useMemo(
        () => filterPositions(positions, search, articles, compartments, serialsByPosition),
        [positions, search, articles, compartments, serialsByPosition]
    );

    const triggerLabel = current
        ? `${articles[current.article_id]?.name || "Artikel"} · ${
              compartments[current.compartment_id]?.code ||
              compartments[current.compartment_id]?.name ||
              "Fach"
          } · ${(current.quantity || 0) - (current.blocked_quantity || 0)}/${current.quantity}`
        : placeholder;

    const countLabel =
        filtered.length === 1 ? "1 Treffer" : `${filtered.length} Treffer`;

    return (
        <Popover
            open={open}
            onOpenChange={(next) => {
                setOpen(next);
                if (!next) setSearch("");
            }}
        >
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    aria-label={current ? `${label}: ${triggerLabel}` : label}
                    className="h-12 w-full justify-between text-left font-normal"
                    data-testid={testId}
                >
                    <span className={`truncate ${current ? "text-foreground" : "text-muted-foreground"}`}>
                        {triggerLabel}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className="w-[--radix-popover-trigger-width] p-0"
                data-testid={`${testId}-popover`}
            >
                <Command shouldFilter={false}>
                    <CommandInput
                        placeholder="Artikel, Nummer oder Fach suchen..."
                        className="h-10"
                        value={search}
                        onValueChange={setSearch}
                        aria-label="Artikel, Hersteller-Nr., Seriennummer oder Fach suchen"
                    />
                    <div
                        className="border-b border-border/60 px-3 py-1 text-xs text-foreground/70"
                        aria-live="polite"
                        data-testid={`${testId}-count`}
                    >
                        {countLabel}
                    </div>
                    <CommandList className="max-h-72">
                        <CommandEmpty>{emptyLabel}</CommandEmpty>
                        <CommandGroup>
                            {filtered.map((p) => {
                                const a = articles[p.article_id];
                                const c = compartments[p.compartment_id];
                                const avail = (p.quantity || 0) - (p.blocked_quantity || 0);
                                const serialHits = matchedSerials(p, search, serialsByPosition);
                                const meta = [
                                    c?.code || c?.name || "Fach",
                                    a?.ean ? `EAN ${a.ean}` : null,
                                    a?.manufacturer_number ? `HNr ${a.manufacturer_number}` : null,
                                    serialHits.length ? `SN ${serialHits.join(", ")}` : null,
                                ].filter(Boolean);
                                return (
                                    <CommandItem
                                        key={p.id}
                                        value={p.id}
                                        onSelect={() => {
                                            onChange(p.id);
                                            setOpen(false);
                                            setSearch("");
                                        }}
                                        data-testid={`${testId}-item-${p.id}`}
                                        className="flex items-start gap-2 py-2"
                                    >
                                        <Check
                                            className={`mt-1 h-4 w-4 ${
                                                value === p.id ? "opacity-100" : "opacity-0"
                                            }`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate text-sm font-medium">
                                                {a?.name || "Artikel"}
                                            </div>
                                            <div className="truncate text-xs text-foreground/70">
                                                {meta.join(" · ")}
                                            </div>
                                        </div>
                                        <div className="whitespace-nowrap text-xs tabular-nums text-foreground/70">
                                            <span className="font-semibold text-foreground">
                                                {avail}
                                            </span>
                                            /{p.quantity}
                                        </div>
                                    </CommandItem>
                                );
                            })}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
